/**
 * Module-level singleton PDF cache.
 *
 * Caches raw ArrayBuffers (not pdf.js PDFDocumentProxy) so they can be safely
 * reused across React mount cycles.  Uses size-based LRU eviction with an
 * adaptive budget derived from `navigator.deviceMemory`.
 */

import { authenticatedFetch } from '../utils/authenticatedFetch';

// ── Adaptive cache budget ────────────────────────────────────────────────────
// 5% of device RAM, clamped between 50 MB and 300 MB.
// navigator.deviceMemory is Chromium-only and rounds to nearest power of 2.
// Safari / Firefox fall back to a conservative 4 GB assumption → 200 MB budget.
const deviceGB = (navigator as any).deviceMemory ?? 4;
const deviceMB = deviceGB * 1024;
const MAX_CACHE_BYTES = Math.min(300, Math.max(50, deviceMB * 0.05)) * 1024 * 1024;

interface CacheEntry {
  data: ArrayBuffer;
  size: number;
  lastAccessed: number;
  pinned: boolean;
}

const cache = new Map<string, CacheEntry>();
const inflightFetches = new Map<string, Promise<ArrayBuffer>>();
let totalBytes = 0;

// ── Helpers ──────────────────────────────────────────────────────────────────

function evictIfNeeded(): void {
  while (totalBytes > MAX_CACHE_BYTES) {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;

    for (const [key, entry] of cache) {
      if (entry.pinned) continue;
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }

    if (!oldestKey) break; // all entries pinned — can't evict
    const entry = cache.get(oldestKey)!;
    totalBytes -= entry.size;
    cache.delete(oldestKey);
  }
}

function getDocumentUrl(documentId: string): string {
  const base = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
  return `${base}/documents/${documentId}/content`;
}

// ── Public API ───────────────────────────────────────────────────────────────

/** Return cached ArrayBuffer or null.  Updates LRU timestamp on hit. */
function get(documentId: string): ArrayBuffer | null {
  const entry = cache.get(documentId);
  if (!entry) return null;
  entry.lastAccessed = Date.now();
  return entry.data;
}

/** Check if a document is cached (without updating timestamp). */
function has(documentId: string): boolean {
  return cache.has(documentId);
}

/** Store an ArrayBuffer in the cache.  Evicts LRU entries if over budget. */
function set(documentId: string, data: ArrayBuffer): void {
  const existing = cache.get(documentId);
  if (existing) {
    totalBytes -= existing.size;
  }

  const size = data.byteLength;
  cache.set(documentId, {
    data,
    size,
    lastAccessed: Date.now(),
    pinned: existing?.pinned ?? false,
  });
  totalBytes += size;
  evictIfNeeded();
}

/**
 * Drop a cached entry. Used by the viewer when it detects the cached bytes
 * don't parse as a PDF (e.g. after a historical truncation) so a retry can
 * refetch rather than re-serve the poisoned buffer.
 */
function evict(documentId: string): void {
  const entry = cache.get(documentId);
  if (entry) {
    totalBytes -= entry.size;
    cache.delete(documentId);
  }
  inflightFetches.delete(documentId);
}

/** Mark a document as non-evictable (e.g. while it's being viewed). */
function pin(documentId: string): void {
  const entry = cache.get(documentId);
  if (entry) entry.pinned = true;
}

/** Mark a document as evictable (e.g. when viewer closes). */
function unpin(documentId: string): void {
  const entry = cache.get(documentId);
  if (entry) entry.pinned = false;
}

/**
 * Fetch a document, cache it, and return the ArrayBuffer.
 * Deduplicates concurrent requests for the same document.
 *
 * @param documentId  The document to fetch.
 * @param preview     When true, appends `?preview=true` (for .doc/.docx PDF conversion).
 * @param onProgress  Optional callback reporting download progress (0–1). Only fires
 *                    when streaming is supported and Content-Length is known.
 */
async function fetchAndCache(
  documentId: string,
  preview?: boolean,
  onProgress?: (fraction: number) => void,
): Promise<ArrayBuffer> {
  // Already cached — return immediately
  const cached = get(documentId);
  if (cached) return cached;

  // Deduplicate concurrent fetches (progress callback only works for the first caller)
  const inflight = inflightFetches.get(documentId);
  if (inflight) return inflight;

  const url = getDocumentUrl(documentId) + (preview ? '?preview=true' : '');
  const promise = authenticatedFetch(url)
    .then(async (response) => {
      // Stream the response to report download progress for large files
      const contentLength = Number(response.headers.get('content-length') || 0);
      if (onProgress && contentLength > 0 && response.body) {
        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let received = 0;

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.byteLength;
          onProgress(Math.min(received / contentLength, 1));
        }

        // Guard against a silently-truncated stream (e.g. the browser aborted
        // the connection mid-read because the caller navigated away). Caching
        // a partial buffer poisons the entry — PDF.js throws InvalidPDFException
        // on every subsequent read and the only escape is a logout.
        if (received !== contentLength) {
          throw new Error(
            `Truncated response for document ${documentId}: expected ${contentLength} bytes, received ${received}`,
          );
        }

        // Assemble chunks into a single ArrayBuffer
        const arrayBuffer = new Uint8Array(received);
        let offset = 0;
        for (const chunk of chunks) {
          arrayBuffer.set(chunk, offset);
          offset += chunk.byteLength;
        }
        set(documentId, arrayBuffer.buffer as ArrayBuffer);
        return arrayBuffer.buffer as ArrayBuffer;
      }

      // Fallback: no streaming (small file or no Content-Length)
      const arrayBuffer = await response.arrayBuffer();
      // If the server did advertise a length, refuse to cache a short body.
      if (contentLength > 0 && arrayBuffer.byteLength !== contentLength) {
        throw new Error(
          `Truncated response for document ${documentId}: expected ${contentLength} bytes, received ${arrayBuffer.byteLength}`,
        );
      }
      set(documentId, arrayBuffer);
      return arrayBuffer;
    })
    .finally(() => {
      inflightFetches.delete(documentId);
    });

  inflightFetches.set(documentId, promise);
  return promise;
}

/**
 * Preload a document in the background using requestIdleCallback.
 * No-op if the document is already cached or being fetched.
 */
function preload(documentId: string, preview?: boolean): void {
  if (has(documentId) || inflightFetches.has(documentId)) return;

  const schedule = typeof requestIdleCallback === 'function'
    ? requestIdleCallback
    : (cb: () => void) => setTimeout(cb, 100);

  schedule(() => {
    // Double-check after idle — might have been cached by a user click in the meantime
    if (!has(documentId) && !inflightFetches.has(documentId)) {
      fetchAndCache(documentId, preview).catch(() => {
        // Preload failures are non-critical — silently ignore
      });
    }
  });
}

/** Clear all cached data (e.g. on logout). */
function clear(): void {
  cache.clear();
  inflightFetches.clear();
  totalBytes = 0;
}

/** Diagnostic stats for console debugging. */
function getStats(): { entries: number; totalMB: number; budgetMB: number; deviceGB: number } {
  return {
    entries: cache.size,
    totalMB: Math.round(totalBytes / (1024 * 1024) * 10) / 10,
    budgetMB: Math.round(MAX_CACHE_BYTES / (1024 * 1024)),
    deviceGB,
  };
}

// Clear cache on logout
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', clear);
}

export const pdfCache = {
  get,
  has,
  set,
  pin,
  unpin,
  evict,
  fetchAndCache,
  preload,
  clear,
  getStats,
};
